/**
 * @fileoverview Assurance Actions Service for managing assurance tasks
 * This service provides a facade over the Firestore database operations
 * to simplify assurance task operations throughout the application.
 * 
 * The service implements the functionality from assuranceactions-html.js
 * with proper TypeScript typing and service-oriented architecture.
 */
import {
  db,
  auth,
  FirebaseTimestamp,
  FirebaseFieldValue
} from '../js/external-js/config/main_config';

// Use Window interface from TenancyContext

/** Type definitions for parameters */
export interface TaskFilter {
  standard?: string;
  overdue?: boolean;
  dueSoon?: boolean;
  assignee?: string;
  startDate?: Date;
  endDate?: Date;
  includeDeleted?: boolean;
}

export interface AssuranceTask {
  taskCreationMetadata?: {
    createdBy: string;
    createdAt: typeof FirebaseTimestamp;
    updatedAt: typeof FirebaseTimestamp;
  };
  // Soft delete fields
  isDeleted?: boolean;
  deletedAt?: typeof FirebaseTimestamp;
  deletedBy?: string;
  checkDetail: {
    active: string;
    assignedTo: string;
    check: string;
    control?: string;
    evidence: string;
    framework: Record<string, { frameName: string; controls: string[] }>;
    meetingNeeded?: string;
    frameworkSkipped?: boolean; // Add support for intentionally skipping framework selection
  };
  checkFrequency: {
    checkDays: string;
    lastCheck?: Record<string, { checkDate: any }>;
    nextCheck?: any;
    reminderDays: string;
    sendReminder: string;
    performedDate?: Date;
  };
  checkHistory?: Record<string, any>;
  history?: Record<string, {
    changeID: string;
    changedBy: string;
    dateChanged: Date;
    fieldsChanged: Record<string, {
      dbFieldName: string;
      fieldName: string;
      newValue: any;
      oldValue: any;
    }>;
  }>;
  // Additional properties for compatibility
  assID?: string;
  taskOwner?: string;
  nextReviewDate?: Date;  // Mapped to checkFrequency.nextCheck
  taskHistory?: Array<{
    changedBy: string;
    changedDate: Date;
    changes: Record<string, any>;
  }>;
  // Additional properties for task performance
  evidence?: any[];
  comments?: string;
  dataClassification?: string;
}

/**
 * Retrieves all assurance tasks from the system
 * 
 * @returns {Promise<AssuranceTask[]>} A promise that resolves to an array of tasks
 */
export const getAllTasks = async (customerID?: string, includeDeleted: boolean = false): Promise<AssuranceTask[]> => {
  try {
    console.log('[AssuranceService] Getting all tasks');
    const companyId = customerID !== undefined ? customerID : window.companyID;
    
    const assuranceRef = db
      .collection('customers')
      .doc(companyId)
      .collection('compliance')
      .doc('assurance');
    
    const assuranceDoc = await assuranceRef.get();
    
    if (assuranceDoc.exists) {
      const tasksData = assuranceDoc.data() || {};
      const tasks: AssuranceTask[] = [];
      
      // Convert the document data into an array of AssuranceTask objects
      for (const key of Object.keys(tasksData)) {
        const task = tasksData[key];
        // Skip deleted tasks unless explicitly requested
        if (task.isDeleted && !includeDeleted) {
          continue;
        }
        tasks.push({
          ...task,
          assID: key
        });
      }
      
      console.log(`[AssuranceService] Found ${tasks.length} tasks`);
      return tasks;
    } else {
      console.log('[AssuranceService] No tasks document found');
      return [];
    }
  } catch (error) {
    console.error('[AssuranceService] Error getting all tasks:', error);
    return [];
  }
};

/**
 * Retrieves tasks for a specific standard
 * 
 * @param {string} standard - The standard to filter by (ISO27001, Essential 8, etc.)
 * @returns {Promise<AssuranceTask[]>} A promise that resolves to an array of filtered tasks
 */
export const getTasksByStandard = async (standard: string): Promise<AssuranceTask[]> => {
  try {
    console.log(`[AssuranceService] Getting tasks for standard: ${standard}`);
    const companyId = window.companyID;
    
    const assuranceRef = db
      .collection('customers')
      .doc(companyId)
      .collection('compliance')
      .doc('assurance');
    
    const assuranceDoc = await assuranceRef.get();
    
    if (assuranceDoc.exists) {
      const tasksData = assuranceDoc.data() || {};
      const tasks: AssuranceTask[] = [];
      
      // Filter and convert the document data into an array of AssuranceTask objects
      for (const key of Object.keys(tasksData)) {
        const task = tasksData[key];
        
        // Check if the task belongs to the specified standard
        const framework0 = task?.checkDetail?.framework?.['0']?.frameName;
        const framework1 = task?.checkDetail?.framework?.['1']?.frameName;
        
        // Check if either framework matches the requested standard
        if (framework0 === standard || framework1 === standard) {
          tasks.push({
            ...task,
            assID: key
          });
        }
      }
      
      console.log(`[AssuranceService] Found ${tasks.length} tasks for ${standard}`);
      return tasks;
    } else {
      console.log('[AssuranceService] No tasks document found');
      return [];
    }
  } catch (error) {
    console.error(`[AssuranceService] Error getting tasks for ${standard}:`, error);
    return [];
  }
};

/**
 * Retrieves a specific task by its ID
 * 
 * @param {string} taskId - The task ID to search for
 * @returns {Promise<AssuranceTask | undefined>} A promise that resolves to the found task or undefined
 */
export const getTask = async (taskId: string): Promise<AssuranceTask | undefined> => {
  try {
    console.log('[AssuranceService] getTask', taskId);
    const companyId = window.companyID;
    
    const assuranceRef = db
      .collection('customers')
      .doc(companyId)
      .collection('compliance')
      .doc('assurance');
    
    const assuranceDoc = await assuranceRef.get();
    
    if (assuranceDoc.exists) {
      const tasksData = assuranceDoc.data() || {};
      
      if (tasksData[taskId]) {
        const task = tasksData[taskId];
        console.log('[AssuranceService] Found task:', taskId);
        return {
          ...task,
          assID: taskId
        };
      } else {
        console.log('[AssuranceService] Task not found:', taskId);
        return undefined;
      }
    } else {
      console.log('[AssuranceService] No tasks document found');
      return undefined;
    }
  } catch (error) {
    console.error('[AssuranceService] Error getting task:', error);
    return undefined;
  }
};

/**
 * Creates a new assurance task
 * 
 * @param {Partial<AssuranceTask>} taskData - The task data to create
 * @returns {Promise<{success: boolean; id: string} | undefined>} A promise that resolves to the API response
 */
export const createTask = async (
  taskData: Partial<AssuranceTask>
): Promise<{success: boolean; id: string} | undefined> => {
  console.log('[AssuranceService] Creating new task');
  
  try {
    const companyId = window.companyID;
    const assuranceRef = db
      .collection('customers')
      .doc(companyId)
      .collection('compliance')
      .doc('assurance');
    
    // Generate an ID for the task
    const taskId = `CST-ASS-${Date.now()}`;
    
    // Create default task structure
    const newTask: AssuranceTask = {
      checkDetail: {
        active: 'true',
        assignedTo: auth.currentUser?.uid || '',
        check: taskData.checkDetail?.check || 'New Task',
        evidence: taskData.checkDetail?.evidence || 'Interview',
        framework: taskData.checkDetail?.framework || {'0': {frameName: 'Custom', controls: []}},
        meetingNeeded: taskData.checkDetail?.meetingNeeded || 'false'
      },
      checkFrequency: {
        checkDays: taskData.checkFrequency?.checkDays || '30',
        reminderDays: taskData.checkFrequency?.reminderDays || '7',
        nextCheck: FirebaseTimestamp.fromDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
        sendReminder: 'false'
      },
      checkHistory: {
        numberPreviousChecks: '0' 
      },
      taskCreationMetadata: {
        createdBy: auth.currentUser?.uid || '',
        createdAt: FirebaseTimestamp.now(),
        updatedAt: FirebaseTimestamp.now()
      }
    };
    
    // Add any task-specific overrides
    if (taskData.checkDetail) {
      newTask.checkDetail = {
        ...newTask.checkDetail,
        ...taskData.checkDetail
      };
    }
    
    if (taskData.checkFrequency) {
      newTask.checkFrequency = {
        ...newTask.checkFrequency,
        ...taskData.checkFrequency
      };
    }
    
    // Set the task in Firestore
    await assuranceRef.set({
      [taskId]: newTask
    }, { merge: true });
    
    console.log('[AssuranceService] Task created successfully with ID:', taskId);
    return {
      success: true,
      id: taskId
    };
  } catch (error) {
    console.error('[AssuranceService] Error creating task:', error);
    return undefined;
  }
};

/**
 * Helper function to create a field change entry
 * 
 * @param {string} dbFieldName - The database field name
 * @param {string} fieldName - The display name of the field
 * @param {any} newValue - The new field value
 * @param {any} oldValue - The old field value
 * @returns {Object} A field change entry object
 */
const createFieldChange = (dbFieldName: string, fieldName: string, newValue: any, oldValue: any) => {
  return {
    dbFieldName,
    fieldName,
    newValue: newValue || '',
    oldValue: oldValue || ''
  };
};

/**
 * Updates an existing assurance task
 * 
 * @param {string} taskId - The ID of the task to update
 * @param {Partial<AssuranceTask>} taskData - The task data to update
 * @returns {Promise<{success: boolean; id: string} | undefined>} A promise that resolves to the API response
 */
export const updateTask = async (
  taskId: string,
  taskData: Partial<AssuranceTask>
): Promise<{success: boolean; id: string} | undefined> => {
  console.log('[AssuranceService] updateTask', taskId);
  
  try {
    const companyId = window.companyID;
    const assuranceRef = db
      .collection('customers')
      .doc(companyId)
      .collection('compliance')
      .doc('assurance');
    
    // Get the current task data
    const assuranceDoc = await assuranceRef.get();
    if (!assuranceDoc.exists || !assuranceDoc.data()?.[taskId]) {
      console.error('[AssuranceService] Task not found:', taskId);
      return undefined;
    }
    
    const currentTask = assuranceDoc.data()?.[taskId];
    const fieldsChanges: Record<string, any> = {};
    let amountOfChanges = 0;
    
    // Track changes to checkDetail fields
    if (taskData.checkDetail) {
      for (const [key, value] of Object.entries(taskData.checkDetail)) {
        if (value !== currentTask.checkDetail?.[key]) {
          amountOfChanges++;
          const keyLength = Object.keys(fieldsChanges).length;
          fieldsChanges[keyLength] = createFieldChange(
            key,
            key.charAt(0).toUpperCase() + key.slice(1),
            value,
            currentTask.checkDetail?.[key]
          );
        }
      }
    }
    
    // Track changes to checkFrequency fields
    if (taskData.checkFrequency) {
      for (const [key, value] of Object.entries(taskData.checkFrequency)) {
        if (key !== 'lastCheck' && value !== currentTask.checkFrequency?.[key]) {
          amountOfChanges++;
          const keyLength = Object.keys(fieldsChanges).length;
          fieldsChanges[keyLength] = createFieldChange(
            key,
            key === 'checkDays' ? 'Check Interval (days)' : 
              key === 'reminderDays' ? 'Reminder (days)' : 
                key === 'nextCheck' ? 'Next Check' : key,
            value,
            currentTask.checkFrequency?.[key]
          );
        }
      }
    }
    
    // If there are no changes, return early
    if (amountOfChanges === 0) {
      console.log('[AssuranceService] No changes to update for task:', taskId);
      return {
        success: true,
        id: taskId
      };
    }
    
    // Get current history or initialize it
    const historyObject = currentTask.history || {};
    const historyLength = Object.keys(historyObject).length;
    const newHistoryLength = historyLength + 1;
    
    // Create update object
    const updateData: Record<string, any> = {
      [`${taskId}.checkDetail`]: {
        ...currentTask.checkDetail,
        ...(taskData.checkDetail || {})
      },
      [`${taskId}.checkFrequency`]: {
        ...currentTask.checkFrequency,
        ...(taskData.checkFrequency || {})
      },
      [`${taskId}.history.${newHistoryLength}`]: {
        changeID: newHistoryLength.toString(),
        changedBy: auth.currentUser?.uid || '',
        dateChanged: new Date(),
        fieldsChanged: fieldsChanges
      }
    };
    
    // Add task history information if provided
    if (taskData.checkHistory) {
      updateData[`${taskId}.checkHistory`] = {
        ...currentTask.checkHistory,
        ...taskData.checkHistory
      };
    }
    
    // Update the task in Firestore
    await assuranceRef.update(updateData);
    
    console.log('[AssuranceService] Task updated successfully:', taskId);
    return {
      success: true,
      id: taskId
    };
  } catch (error) {
    console.error('[AssuranceService] Error updating task:', error);
    return undefined;
  }
};

/**
 * Performs (completes) a task
 * 
 * @param {string} taskId - The ID of the task to perform
 * @param {Partial<AssuranceTask>} taskData - Additional data for the task performance
 * @returns {Promise<{success: boolean; id: string} | undefined>} A promise that resolves to the API response
 */
export const performTask = async (
  taskId: string,
  taskData: Partial<AssuranceTask>
): Promise<{success: boolean; id: string} | undefined> => {
  console.log('[AssuranceService] performTask', taskId);
  
  try {
    const companyId = window.companyID;
    const assuranceRef = db
      .collection('customers')
      .doc(companyId)
      .collection('compliance')
      .doc('assurance');
    
    // Get the current task data
    const assuranceDoc = await assuranceRef.get();
    if (!assuranceDoc.exists || !assuranceDoc.data()?.[taskId]) {
      console.error('[AssuranceService] Task not found:', taskId);
      return undefined;
    }
    
    const currentTask = assuranceDoc.data()?.[taskId];
    const currentAssuranceTaskCheckFrequencyMap = currentTask.checkFrequency || {};
    const currentAssuranceTaskCheckHistoryMap = currentTask.checkHistory || {};
    
    // Find the highest check number
    let numberPreviousChecks = 0;
    if (currentAssuranceTaskCheckHistoryMap) {
      const existingChecks = Object.keys(currentAssuranceTaskCheckHistoryMap)
        .filter(key => !isNaN(parseInt(key)))
        .map(key => parseInt(key));
      if (existingChecks.length > 0) {
        numberPreviousChecks = Math.max(...existingChecks);
      }
    }
    
    const newNumberPreviousChecks = numberPreviousChecks + 1;
    const checkDays = parseInt(currentAssuranceTaskCheckFrequencyMap.checkDays) || 30;
    
    // Calculate next check based on current date
    const performedDate = taskData.checkFrequency?.performedDate || new Date();
    const lastCheck = new Date(performedDate);
    const newNextCheck = new Date(performedDate);
    newNextCheck.setDate(newNextCheck.getDate() + checkDays);
    
    // Handle evidence file storage if needed
    // Note: In a real implementation, you would handle file uploads separately
    const evidenceArray = taskData.evidence || [];
    
    // Create update data
    const updateData: Record<string, any> = {
      [`${taskId}.checkFrequency.lastCheck.${newNumberPreviousChecks}`]: {
        checkDate: FirebaseTimestamp.fromDate(lastCheck)
      },
      [`${taskId}.checkFrequency.nextCheck`]: FirebaseTimestamp.fromDate(newNextCheck),
      [`${taskId}.checkHistory.numberPreviousChecks`]: newNumberPreviousChecks.toString(),
      [`${taskId}.checkHistory.${newNumberPreviousChecks}`]: {
        assignedTo: taskData.checkDetail?.assignedTo || currentTask.checkDetail?.assignedTo || auth.currentUser?.uid,
        check: currentTask.checkDetail?.check || '',
        comments: taskData.comments || '',
        dataClassification: taskData.dataClassification || 'Confidential',
        evidence: evidenceArray,
        lastCheck: FirebaseTimestamp.now(),
        checkStatus: 'complete',
        performedDate: FirebaseTimestamp.fromDate(lastCheck),
        performedBy: auth.currentUser?.uid || ''
      }
    };
    
    // Update the task in Firestore
    await assuranceRef.update(updateData);
    
    console.log('[AssuranceService] Task performed successfully:', taskId);
    return {
      success: true,
      id: taskId
    };
  } catch (error) {
    console.error('[AssuranceService] Error performing task:', error);
    return undefined;
  }
};

/**
 * Soft deletes an assurance task (marks as deleted but keeps data)
 * 
 * @param {string} taskId - The ID of the task to soft delete
 * @returns {Promise<{success: boolean; id: string} | undefined>} A promise that resolves to the API response
 */
export const softDeleteTask = async (
  taskId: string
): Promise<{success: boolean; id: string} | undefined> => {
  console.log('[AssuranceService] softDeleteTask', taskId);
  
  try {
    const companyId = window.companyID;
    const assuranceRef = db
      .collection('customers')
      .doc(companyId)
      .collection('compliance')
      .doc('assurance');
    
    // Get the current data to verify the task exists
    const assuranceDoc = await assuranceRef.get();
    if (!assuranceDoc.exists || !assuranceDoc.data()?.[taskId]) {
      console.error('[AssuranceService] Task not found:', taskId);
      return undefined;
    }
    
    // Soft delete the task by marking it as deleted
    await assuranceRef.update({
      [`${taskId}.isDeleted`]: true,
      [`${taskId}.deletedAt`]: FirebaseTimestamp.now(),
      [`${taskId}.deletedBy`]: auth.currentUser?.uid || ''
    });
    
    console.log('[AssuranceService] Task soft deleted successfully:', taskId);
    return { success: true, id: taskId };
  } catch (error) {
    console.error('[AssuranceService] Error soft deleting task:', error);
    return undefined;
  }
};

/**
 * Restores a soft deleted assurance task
 * 
 * @param {string} taskId - The ID of the task to restore
 * @returns {Promise<{success: boolean; id: string} | undefined>} A promise that resolves to the API response
 */
export const restoreTask = async (
  taskId: string
): Promise<{success: boolean; id: string} | undefined> => {
  console.log('[AssuranceService] restoreTask', taskId);
  
  try {
    const companyId = window.companyID;
    const assuranceRef = db
      .collection('customers')
      .doc(companyId)
      .collection('compliance')
      .doc('assurance');
    
    // Get the current data to verify the task exists
    const assuranceDoc = await assuranceRef.get();
    if (!assuranceDoc.exists || !assuranceDoc.data()?.[taskId]) {
      console.error('[AssuranceService] Task not found:', taskId);
      return undefined;
    }
    
    // Restore the task by removing deleted flags
    await assuranceRef.update({
      [`${taskId}.isDeleted`]: false,
      [`${taskId}.deletedAt`]: FirebaseFieldValue.delete(),
      [`${taskId}.deletedBy`]: FirebaseFieldValue.delete()
    });
    
    console.log('[AssuranceService] Task restored successfully:', taskId);
    return { success: true, id: taskId };
  } catch (error) {
    console.error('[AssuranceService] Error restoring task:', error);
    return undefined;
  }
};

/**
 * Deletes an assurance task
 * 
 * @param {string} taskId - The ID of the task to delete
 * @returns {Promise<{success: boolean} | undefined>} A promise that resolves to the API response
 */
export const deleteTask = async (
  taskId: string
): Promise<{success: boolean} | undefined> => {
  console.log('[AssuranceService] deleteTask', taskId);
  
  try {
    const companyId = window.companyID;
    const assuranceRef = db
      .collection('customers')
      .doc(companyId)
      .collection('compliance')
      .doc('assurance');
    
    // Get the current data to verify the task exists
    const assuranceDoc = await assuranceRef.get();
    if (!assuranceDoc.exists || !assuranceDoc.data()?.[taskId]) {
      console.error('[AssuranceService] Task not found:', taskId);
      return undefined;
    }
    
    // Delete the task using FieldValue.delete()
    await assuranceRef.update({
      [taskId]: FirebaseFieldValue.delete()
    });
    
    console.log('[AssuranceService] Task deleted successfully:', taskId);
    return { success: true };
  } catch (error) {
    console.error('[AssuranceService] Error deleting task:', error);
    return undefined;
  }
};

/**
 * Gets the task history for a specific assurance task
 * 
 * @param {string} taskId - The ID of the task to get history for
 * @returns {Promise<Array<{changedBy: string; changedDate: Date; changes: Record<string, any>}>>} A promise that resolves to the task history
 */
export const getTaskHistory = async (
  taskId: string
): Promise<Array<{changedBy: string; changedDate: Date; changes: Record<string, any>}>> => {
  try {
    console.log('[AssuranceService] Getting task history for', taskId);
    const task = await getTask(taskId);
    
    if (task && task.history) {
      // Convert the history object to an array of history items
      const historyArray = Object.values(task.history).map(item => ({
        changedBy: item.changedBy,
        changedDate: item.dateChanged,
        changes: item.fieldsChanged || {}
      }));
      
      // Sort by date, most recent first
      historyArray.sort((a, b) => 
        (new Date(b.changedDate).getTime() - new Date(a.changedDate).getTime())
      );
      
      console.log(`[AssuranceService] Found ${historyArray.length} history entries`);
      return historyArray;
    }
    
    console.log('[AssuranceService] No history found for task');
    return [];
  } catch (error) {
    console.error('[AssuranceService] Error getting task history:', error);
    return [];
  }
};

/**
 * Gets filtered tasks based on provided criteria
 * 
 * @param {TaskFilter} filter - Filter criteria including standard, overdue status, etc.
 * @returns {Promise<AssuranceTask[]>} A promise that resolves to filtered tasks
 */
export const getFilteredTasks = async (
  filter: TaskFilter
): Promise<AssuranceTask[]> => {
  try {
    console.log('[AssuranceService] Getting filtered tasks', filter);
    
    // First get all tasks or tasks by standard
    const tasks = filter.standard 
      ? await getTasksByStandard(filter.standard)
      : await getAllTasks(undefined, filter.includeDeleted);
    
    // Apply filters
    const filteredTasks = tasks.filter(task => {
      // Map nextCheck to nextReviewDate for compatibility
      const nextReviewDate = task.checkFrequency?.nextCheck instanceof FirebaseTimestamp
        ? task.checkFrequency.nextCheck.toDate()
        : (task.nextReviewDate || null);
      
      // Filter by assignee if specified
      if (filter.assignee && filter.assignee !== 'allAssignees' && 
          task.checkDetail?.assignedTo !== filter.assignee) {
        return false;
      }

      // Filter by overdue status if specified
      if (filter.overdue) {
        if (!nextReviewDate || nextReviewDate > new Date()) {
          return false;
        }
      }

      // Filter by due soon status if specified
      if (filter.dueSoon) {
        if (!nextReviewDate) return false;
        
        const now = new Date();
        const twoWeeksFromNow = new Date();
        twoWeeksFromNow.setDate(now.getDate() + 14);
        
        if (nextReviewDate < now || nextReviewDate > twoWeeksFromNow) {
          return false;
        }
      }

      // Filter by date range if specified
      if (filter.startDate && filter.endDate) {
        if (!nextReviewDate || nextReviewDate < filter.startDate || nextReviewDate > filter.endDate) {
          return false;
        }
      }

      return true;
    });
    
    console.log(`[AssuranceService] Found ${filteredTasks.length} tasks after filtering`);
    return filteredTasks;
  } catch (error) {
    console.error('[AssuranceService] Error filtering tasks:', error);
    return [];
  }
};

/** Default export of all assurance service functions */
export default {
  getAllTasks,
  getTasksByStandard,
  getTask,
  createTask,
  updateTask,
  performTask,
  deleteTask,
  softDeleteTask,
  restoreTask,
  getTaskHistory,
  getFilteredTasks,
};
